import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  User, LogOut, Award, Gift, Calendar, Package, Truck, 
  MapPin, Clock, Phone, Mail, Pencil, Check, X, ChevronRight, Crown, Repeat, Pause, Play, Trash2, CreditCard, ExternalLink, FileText, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import SakuraChatbot from "@/components/SakuraChatbot";
import SakuraTree from "@/components/SakuraTree";
import ReferralSection from "@/components/ReferralSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [recurringSubscriptions, setRecurringSubscriptions] = useState([]);
  const [deliverySlips, setDeliverySlips] = useState([]);
  const [viewingSlip, setViewingSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [newRecurring, setNewRecurring] = useState({ zone_id: "", day_of_week: "", time_slot: "", frequency: "weekly" });
  const [zoneConfig, setZoneConfig] = useState(null);
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    address: "",
    phone: ""
  });

  const fetchDeliverySlips = async () => {
    try {
      const res = await axios.get(`${API}/customer/delivery-slips`, { withCredentials: true });
      setDeliverySlips(res.data || []);
    } catch (error) {
      // Silently fail - user might not be a pro customer
      console.log("No delivery slips or not a pro customer");
    }
  };

  const fetchRecurringSubscriptions = async () => {
    try {
      const res = await axios.get(`${API}/customer/recurring-subscriptions`, { withCredentials: true });
      setRecurringSubscriptions(res.data || []);
    } catch (error) {
      console.error("Error fetching recurring subscriptions:", error);
    }
  };

  const fetchPaymentLinks = async () => {
    try {
      const res = await axios.get(`${API}/customer/payment-links`, { withCredentials: true });
      setPaymentLinks(res.data || []);
    } catch (error) {
      console.error("Error fetching payment links:", error);
    }
  };

  const fetchZoneConfig = async (zoneId) => {
    try {
      const res = await axios.get(`${API}/zone-config/${zoneId}`);
      setZoneConfig(res.data);
    } catch (error) {
      console.error("Error fetching zone config:", error);
      setZoneConfig(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerRes, bookingsRes, transactionsRes] = await Promise.all([
          axios.get(`${API}/customer/auth/me`, { withCredentials: true }),
          axios.get(`${API}/customer/bookings`, { withCredentials: true }),
          axios.get(`${API}/customer/transactions`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        
        setCustomer(customerRes.data);
        if (customerRes.data?.email) localStorage.setItem("customer_email", customerRes.data.email);
        setBookings(bookingsRes.data);
        setTransactions(transactionsRes.data || []);
        setProfileForm({
          first_name: customerRes.data.first_name || "",
          last_name: customerRes.data.last_name || "",
          address: customerRes.data.address || "",
          phone: customerRes.data.phone || ""
        });
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/connexion");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchRecurringSubscriptions();
    fetchPaymentLinks();
    fetchDeliverySlips();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/customer/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem("customer_email");
      toast.success("Déconnexion réussie");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await axios.put(`${API}/customer/profile`, profileForm, {
        withCredentials: true
      });
      setCustomer(response.data.customer);
      setEditingProfile(false);
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleCreateRecurring = async () => {
    if (!newRecurring.zone_id || newRecurring.day_of_week === "" || !newRecurring.time_slot) {
      toast.error("Choisis une zone, un jour et un créneau horaire");
      return;
    }
    try {
      const response = await axios.post(
        `${API}/customer/recurring-subscriptions?zone_id=${newRecurring.zone_id}&day_of_week=${newRecurring.day_of_week}&time_slot=${newRecurring.time_slot}&frequency=${newRecurring.frequency}`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowRecurringForm(false);
      setNewRecurring({ zone_id: "", day_of_week: "", time_slot: "", frequency: "weekly" });
      setZoneConfig(null);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleToggleRecurring = async (subscriptionId) => {
    try {
      const response = await axios.put(
        `${API}/customer/recurring-subscriptions/${subscriptionId}/toggle`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteRecurring = async (subscriptionId) => {
    if (!window.confirm("Annuler cet abonnement ? Les réservations futures seront annulées.")) return;
    try {
      const response = await axios.delete(
        `${API}/customer/recurring-subscriptions/${subscriptionId}`,
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchRecurringSubscriptions();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      confirmed: "bg-green-100 text-green-700",
      delivered: "bg-blue-100 text-blue-700",
      cancelled: "bg-red-100 text-red-700"
    };
    const labels = {
      pending: "En attente",
      confirmed: "Confirmé",
      delivered: "Livré",
      cancelled: "Annulé"
    };
    return <Badge className={styles[status] || "bg-slate-100"}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-pink-50 to-white">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const rewardsAvailable = (customer?.rewards_earned || 0) - (customer?.rewards_used || 0);
  const loyaltyProgress = ((customer?.loyalty_balance || 0) / 50) * 100;

  // Separate bookings
  const upcomingBookings = bookings.filter(b => 
    b.status !== "delivered" && b.status !== "cancelled"
  );
  const pastBookings = bookings.filter(b => 
    b.status === "delivered" || b.status === "cancelled"
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <nav className="sticky top-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-12 w-auto cursor-pointer"
            onClick={() => navigate("/")}
          />
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} className="bg-primary hover:bg-primary/90">
              Nouvelle réservation
            </Button>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bonjour {customer?.first_name} 👋
            </h1>
            {customer?.is_vip && (
              <Badge className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-sm px-3 py-1 flex items-center gap-1.5 shadow-lg">
                <Crown className="w-4 h-4" />
                VIP - Réservation prioritaire
              </Badge>
            )}
          </div>
          <p className="text-slate-600 mt-1">
            Bienvenue dans ton espace client Pressing Sakura
            {customer?.is_vip && " - Tu peux réserver même sur les créneaux complets !"}
          </p>
        </div>

        {/* Transaction History Card - Moved above Loyalty */}
        {transactions.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Historique (2 derniers mois)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {transactions.map((t) => (
                  <div 
                    key={t.id} 
                    className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                      t.type === "sakura_draw"
                        ? (t.prize && t.prize !== "nothing" ? "bg-pink-100" : "bg-slate-50")
                        : t.type === "referral_bonus"
                          ? "bg-purple-50"
                          : t.type === "loyalty" 
                            ? (t.rewards_earned > 0 ? "bg-yellow-50" : "bg-pink-50")
                            : t.type === "invoice" || t.type === "payment"
                              ? "bg-blue-50"
                              : (t.action === "add" ? "bg-green-50" : "bg-orange-50")
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>
                        {t.type === "sakura_draw" 
                          ? "🌸" 
                          : t.type === "referral_bonus"
                            ? "🤝"
                            : t.type === "shirts" 
                              ? "👔" 
                              : t.type === "loyalty" 
                                ? (t.rewards_earned > 0 ? "🎁" : "🧾") 
                                : t.type === "invoice" || t.type === "payment"
                                  ? "📄"
                                  : "💰"}
                      </span>
                      <div>
                        <span className={
                          t.type === "sakura_draw"
                            ? (t.prize && t.prize !== "nothing" ? "text-pink-700" : "text-slate-600")
                            : t.type === "referral_bonus"
                              ? "text-purple-700"
                              : t.type === "loyalty" 
                                ? (t.rewards_earned > 0 ? "text-yellow-700" : "text-pink-700")
                                : t.type === "invoice" || t.type === "payment"
                                  ? "text-blue-700"
                                  : (t.action === "add" ? "text-green-700" : "text-orange-700")
                        }>
                          {t.description}
                        </span>
                        {t.type === "referral_bonus" && (
                          <p className="text-xs text-purple-600">+{t.amount}€ crédités</p>
                        )}
                        {(t.type === "invoice" || t.type === "payment") && t.amount && (
                          <p className="text-xs text-blue-600">{t.amount}€</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {format(new Date(t.created_at), "dd/MM/yy", { locale: fr })}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Loyalty Card */}
          <Card className="lg:col-span-2 overflow-hidden">
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-8 h-8" />
                <div>
                  <h2 className="text-xl font-bold">Programme Fidélité</h2>
                  <p className="text-pink-100 text-sm">5€ offerts tous les 50€ dépensés</p>
                  <p className="text-pink-200 text-xs mt-1">(Hors pack chemises et robe de mariée)</p>
                </div>
              </div>
              
              {rewardsAvailable > 0 && (
                <div className="bg-white/20 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Gift className="w-6 h-6" />
                    <span className="text-lg font-bold">
                      {rewardsAvailable} remise{rewardsAvailable > 1 ? "s" : ""} de 5€ obtenue{rewardsAvailable > 1 ? "s" : ""} = {rewardsAvailable * 5}€
                    </span>
                  </div>
                  <p className="text-pink-100 text-sm mt-1">
                    Remise appliquée automatiquement sur ta facture !
                  </p>
                </div>
              )}
              
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Progression</span>
                  <span>{(customer?.loyalty_balance || 0).toFixed(2)}€ / 50€</span>
                </div>
                <div className="h-3 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${loyaltyProgress}%` }}
                  />
                </div>
                <p className="text-pink-100 text-sm mt-2">
                  Plus que {(50 - (customer?.loyalty_balance || 0)).toFixed(2)}€ pour ta prochaine récompense !
                </p>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Total dépensé : <strong>{(customer?.total_spent || 0).toFixed(2)}€</strong></span>
                <span>Récompenses gagnées : <strong>{customer?.rewards_earned || 0}</strong></span>
              </div>
            </CardContent>
          </Card>

          {/* Prepaid Shirts Card */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">👔</span>
                <div>
                  <h2 className="text-xl font-bold">Chemises Prépayées</h2>
                  <p className="text-blue-100 text-sm">60€ les 20 chemises, t-shirts ou polos</p>
                </div>
              </div>
              
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <span className="text-4xl font-bold">{customer?.prepaid_shirts || 0}</span>
                <span className="text-lg ml-2">chemise{(customer?.prepaid_shirts || 0) !== 1 ? "s" : ""} restante{(customer?.prepaid_shirts || 0) !== 1 ? "s" : ""}</span>
              </div>
              
              {(customer?.prepaid_shirts || 0) === 0 && (
                <p className="text-blue-100 text-sm mt-3 text-center">
                  💡 Demande ton forfait prépayé et économise !
                </p>
              )}
            </div>
          </Card>

          {/* Free Treatments Card */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🧴</span>
                <div>
                  <h2 className="text-xl font-bold">Traitements Offerts</h2>
                  <p className="text-purple-100 text-sm">Anti-acariens, anti-mites, imperméabilisant...</p>
                </div>
              </div>
              
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <span className="text-4xl font-bold">{customer?.free_treatments || 0}</span>
                <span className="text-lg ml-2">traitement{(customer?.free_treatments || 0) !== 1 ? "s" : ""} en stock</span>
              </div>
              
              {(customer?.free_treatments || 0) === 0 && (
                <p className="text-purple-100 text-sm mt-3 text-center">
                  🌸 Joue au Cerisier Sakura pour en gagner !
                </p>
              )}
            </div>
          </Card>

          {/* Prepaid Balance Card */}
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">💰</span>
                <div>
                  <h2 className="text-xl font-bold">Prépaiement</h2>
                  <p className="text-emerald-100 text-sm">Ton solde disponible</p>
                </div>
              </div>
              
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <span className="text-4xl font-bold">{(customer?.prepaid_balance || 0).toFixed(2)}</span>
                <span className="text-lg ml-1">€</span>
              </div>
            </div>
          </Card>

          {/* Sakura Tree - Loyalty Game */}
          <Card className="lg:col-span-2 overflow-visible border-0 shadow-none bg-transparent">
            <SakuraTree />
          </Card>

          {/* Snake Sakura Game Button */}
          <Card className="lg:col-span-2 border-pink-200 bg-gradient-to-r from-pink-50 to-pink-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🎮</span>
                  <div>
                    <h3 className="font-semibold text-pink-700">Snake Sakura</h3>
                    <p className="text-xs text-pink-600">Aide l'arbre à s'épanouir et monte sur le podium !</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/snake-sakura")}
                  className="bg-pink-500 hover:bg-pink-600"
                >
                  Jouer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Referral Program - Always show */}
          <div className="lg:col-span-2">
            <ReferralSection customer={customer} />
          </div>

          {/* Promo Banner - Traitement Hygiénisant */}
          <Card className="lg:col-span-2 overflow-hidden border-0 shadow-lg">
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/5he773hk_ChatGPT%20Image%2029%20juil.%202026%2C%2019_51_39.png"
              alt="Traitement Hygiénisant Sakura - Désinfection, Anti-bactérien, Fraîcheur longue durée"
              className="w-full h-auto rounded-xl"
            />
          </Card>

          {/* Payment Links Card */}
          {paymentLinks.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Factures à régler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {paymentLinks.map((link) => (
                    <div 
                      key={link.id}
                      className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-lg text-amber-700">{link.amount.toFixed(2)}€</span>
                        </div>
                        <p className="text-sm text-slate-700 truncate">{link.description}</p>
                        <p className="text-xs text-slate-500">
                          Ajouté le {format(new Date(link.created_at), "d MMMM yyyy", { locale: fr })}
                        </p>
                      </div>
                      <Button
                        onClick={() => window.open(link.payment_url, "_blank")}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Payer
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Delivery Slips Card (Pro customers only) */}
          {deliverySlips.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Mes bons de livraison
                  <span className="ml-auto text-sm font-semibold text-orange-600" data-testid="delivery-slips-unpaid-total">
                    Reste à payer : {deliverySlips.filter(s => !s.is_paid).reduce((sum, s) => sum + (s.total || 0), 0).toFixed(2)}€
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {deliverySlips.map((slip) => (
                    <div 
                      key={slip.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${slip.is_paid ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-lg">{slip.slip_number}</span>
                          {slip.is_paid ? (
                            <Badge className="bg-green-500 text-white">Payé</Badge>
                          ) : (
                            <Badge className="bg-amber-500 text-white">À payer</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{slip.items?.length || 0} article(s) - {slip.total?.toFixed(2)}€</p>
                        <p className="text-xs text-slate-500">
                          {slip.date}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => setViewingSlip(slip)}
                        className="shrink-0"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profile Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Mon profil
                </CardTitle>
                {!editingProfile && (
                  <Button variant="ghost" size="sm" onClick={() => setEditingProfile(true)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingProfile ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Prénom</Label>
                      <Input
                        value={profileForm.first_name}
                        onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nom</Label>
                      <Input
                        value={profileForm.last_name}
                        onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                        className="h-8"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Adresse</Label>
                    <Input
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone</Label>
                    <Input
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      className="h-8"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditingProfile(false)} className="flex-1">
                      <X className="w-4 h-4 mr-1" /> Annuler
                    </Button>
                    <Button size="sm" onClick={handleUpdateProfile} className="flex-1 bg-primary">
                      <Check className="w-4 h-4 mr-1" /> Sauver
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-base">
                    {customer?.first_name} {customer?.last_name}
                  </p>
                  {customer?.address && (
                    <p className="flex items-start gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5 text-slate-400" />
                      {customer.address}
                    </p>
                  )}
                  {customer?.phone && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {customer.phone}
                    </p>
                  )}
                  {customer?.email && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {customer.email}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recurring Subscriptions Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Repeat className="w-5 h-5 text-primary" />
              Ramassage & Livraison récurrents
            </h2>
            <Button 
              onClick={() => setShowRecurringForm(!showRecurringForm)}
              className="bg-primary hover:bg-primary/90"
              size="sm"
            >
              {showRecurringForm ? "Annuler" : "+ Nouveau récurrent"}
            </Button>
          </div>

          {/* New Recurring Form */}
          {showRecurringForm && (
            <Card className="mb-4 border-primary/20 bg-pink-50/50">
              <CardContent className="p-4">
                <p className="text-sm text-slate-600 mb-4">
                  Choisis un jour de la semaine pour un ramassage et une livraison automatiques chaque semaine. 
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm">Zone</Label>
                    <Select 
                      value={newRecurring.zone_id} 
                      onValueChange={(v) => {
                        setNewRecurring({...newRecurring, zone_id: v, day_of_week: "", time_slot: ""});
                        fetchZoneConfig(v);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir une zone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lyon">Lyon</SelectItem>
                        <SelectItem value="saint-rambert">Saint-Rambert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Jour</Label>
                    <Select 
                      value={newRecurring.day_of_week} 
                      onValueChange={(v) => setNewRecurring({...newRecurring, day_of_week: v, time_slot: ""})}
                      disabled={!zoneConfig}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un jour" />
                      </SelectTrigger>
                      <SelectContent>
                        {zoneConfig?.allowed_days_options?.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Créneau</Label>
                    <Select 
                      value={newRecurring.time_slot} 
                      onValueChange={(v) => setNewRecurring({...newRecurring, time_slot: v})}
                      disabled={!newRecurring.day_of_week}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        {zoneConfig?.day_time_slots_labels?.[newRecurring.day_of_week]?.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Fréquence</Label>
                    <Select 
                      value={newRecurring.frequency} 
                      onValueChange={(v) => setNewRecurring({...newRecurring, frequency: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Chaque semaine</SelectItem>
                        <SelectItem value="biweekly">1 semaine sur 2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Button 
                    onClick={handleCreateRecurring} 
                    className="w-full bg-primary"
                    disabled={!newRecurring.zone_id || !newRecurring.day_of_week || !newRecurring.time_slot}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Valider
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recurring List */}
          {recurringSubscriptions.length > 0 ? (
            <div className="space-y-3">
              {recurringSubscriptions.map((sub) => (
                <Card key={sub.id} className={`${sub.is_active ? "" : "opacity-60"}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${sub.is_active ? "bg-pink-100" : "bg-gray-100"}`}>
                          <Repeat className={`w-6 h-6 ${sub.is_active ? "text-pink-600" : "text-gray-400"}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">
                            {sub.frequency === "biweekly" ? "Un " : "Tous les "}{sub.day_name}{sub.frequency === "biweekly" ? " sur deux" : "s"}
                          </p>
                          <p className="text-sm text-slate-600">
                            Zone : {sub.zone_name}
                            {sub.frequency === "biweekly" && <Badge className="ml-2 bg-purple-100 text-purple-700">1 sem./2</Badge>}
                            {!sub.is_active && <Badge className="ml-2 bg-gray-200 text-gray-600">En pause</Badge>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleRecurring(sub.id)}
                          title={sub.is_active ? "Mettre en pause" : "Réactiver"}
                        >
                          {sub.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteRecurring(sub.id)}
                          className="text-red-500 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-slate-50">
              <CardContent className="p-6 text-center text-slate-500">
                <Repeat className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Pas encore de récurrent</p>
                <p className="text-sm">Programme un ramassage & livraison automatique chaque semaine !</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Upcoming Bookings */}
        {upcomingBookings.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Réservations en cours
            </h2>
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <Card key={booking.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          {booking.pickup_date && (
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                                <Package className="w-4 h-4 text-pink-600" />
                              </div>
                              <div>
                                <p className="font-medium">Ramassage</p>
                                <p className="text-sm text-slate-600">
                                  {format(new Date(booking.pickup_date), "EEEE d MMMM", { locale: fr })} • {booking.pickup_time}
                                </p>
                              </div>
                            </div>
                          )}
                          {booking.delivery_date && (
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <Truck className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium">Livraison</p>
                                <p className="text-sm text-slate-600">
                                  {format(new Date(booking.delivery_date), "EEEE d MMMM", { locale: fr })} • {booking.delivery_time}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          {getStatusBadge(booking.status)}
                          {booking.has_surcharge && (
                            <Badge className="mt-2 bg-amber-100 text-amber-700">
                              Express +{booking.surcharge_percent || 50}%
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Action buttons for pending/confirmed bookings */}
                      {(booking.status === "pending" || booking.status === "confirmed") && (
                        <div className="flex gap-2 pt-2 border-t border-slate-100">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => navigate(`/manage/${booking.id}`)}
                          >
                            <Pencil className="w-4 h-4 mr-1" />
                            Modifier
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm("Êtes-vous sûr de vouloir annuler cette réservation ?")) return;
                              try {
                                await axios.put(`${API}/bookings/${booking.id}/cancel`, {}, { withCredentials: true });
                                toast.success("Réservation annulée");
                                // Refresh bookings
                                const res = await axios.get(`${API}/customer/bookings`, { withCredentials: true });
                                setBookings(res.data);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
                              }
                            }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              Historique
            </h2>
            <div className="space-y-2">
              {pastBookings.slice(0, 10).map((booking) => (
                <div 
                  key={booking.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${booking.status === "delivered" ? "bg-green-500" : "bg-red-500"}`} />
                    <span className="text-sm">
                      {booking.pickup_date && format(new Date(booking.pickup_date), "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  {getStatusBadge(booking.status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {bookings.length === 0 && (
          <Card className="mt-8">
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Aucune réservation</h3>
              <p className="text-slate-500 mb-4">Tu n'as pas encore effectué de réservation.</p>
              <Button onClick={() => navigate("/")} className="bg-primary">
                Réserver maintenant
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Sakura Chatbot - Fixed position */}
      <div className="fixed bottom-6 right-6 z-50">
        <SakuraChatbot />
      </div>

      {/* Delivery Slip Detail Dialog */}
      {viewingSlip && (
        <Dialog open={!!viewingSlip} onOpenChange={() => setViewingSlip(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Bon de livraison {viewingSlip.slip_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">{viewingSlip.date}</span>
                {viewingSlip.is_paid ? (
                  <Badge className="bg-green-500 text-white">Payé</Badge>
                ) : (
                  <Badge className="bg-amber-500 text-white">À payer</Badge>
                )}
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2">N° Lot</th>
                      <th className="text-left p-2">Article</th>
                      <th className="text-center p-2">Qté</th>
                      <th className="text-right p-2">Prix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingSlip.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2 font-mono text-xs">{item.marking_number || '-'}</td>
                        <td className="p-2">{item.article}</td>
                        <td className="p-2 text-center">{item.quantity}</td>
                        <td className="p-2 text-right">{(item.total ?? item.unit_price * item.quantity).toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-bold">
                    {viewingSlip.discount_amount > 0 && (
                      <tr className="font-normal text-sm" data-testid="customer-slip-discount">
                        <td colSpan="3" className="p-2 text-right">Sous-total : {viewingSlip.subtotal?.toFixed(2)}€ — Remise{viewingSlip.discount_type === "percent" ? ` (${viewingSlip.discount_value}%)` : ""} :</td>
                        <td className="p-2 text-right text-green-700">-{viewingSlip.discount_amount.toFixed(2)}€</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="3" className="p-2 text-right">Total :</td>
                      <td className="p-2 text-right text-lg">{viewingSlip.total?.toFixed(2)}€</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setViewingSlip(null)}>Fermer</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CustomerDashboard;
